import React, { useContext } from "react";
import { Plan, PlanCategoryType, PlanSNPType } from "../../../@types";
import { makePlanDisplayId, PreviewContext } from "../../../helpers";
import { useTranslate } from "../../../helpers/intlHooks";
import { isMaPlanType, isMapdPlanType } from "../../../helpers/planTypes";
import { FocusableHeader, PrintButton } from "../../../components";
import { getPlanName } from "../../../helpers/planHelpers";
import { AppStore } from "../../../app/store";

export const planDetailsPrintButtonTestId = "plan-details-print-button";

export const PlanInfo: React.FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppStore.AppContext);
  const previewMode = React.useContext(PreviewContext);
  let planTypeKey;
  let snpTypeKey;

  // we don't display the plan type or the snp type for original medicare
  if (plan.category !== PlanCategoryType.ORIGINAL_MEDICARE) {
    if (isMaPlanType(plan.plan_type)) {
      planTypeKey = "plan_details.MA";
    } else if (isMapdPlanType(plan.plan_type)) {
      planTypeKey = "plan_details.MAPD";
    } else {
      planTypeKey = "plan_details.PDP";
    }

    if (plan.category === PlanCategoryType.REGIONAL_PPO) {
      planTypeKey = "plan_details.regional_ppo";
    } else if (plan.category === PlanCategoryType.MEDICARE_MEDICAID_PLAN) {
      planTypeKey = "plan_details.medicare_medicaid_plan";
    }

    if (plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE) {
      snpTypeKey = "plan_details.dual_eligible";
    } else if (plan.snp_type === PlanSNPType.SNP_TYPE_INSTITUTIONAL) {
      snpTypeKey = "plan_details.institutional";
    } else if (plan.snp_type === PlanSNPType.SNP_TYPE_CHRONIC_OR_DISABLING) {
      snpTypeKey = "plan_details.chronic_condition";
    }
  }

  return (
    <div className="PlanDetailsPagePlanInfo">
      <div className={previewMode ? "" : "PlanDetailsPagePlanInfo__leftCol"}>
        <div className="PlanDetailsPagePlanInfo__planName">
          <FocusableHeader
            text={getPlanName(plan, language)}
            className="e2e-plan-details-plan-header"
            ignoreFocus={previewMode}
          />
          <h2 className="ds-text-heading--sm">{plan.organization_name}</h2>
        </div>
        <ul className="ds-c-list ds-c-list--bare">
          {planTypeKey && (
            <>
              <li>
                <span className="PlanDetailsPagePlanInfo__label">
                  {t("plan_details.plan_type")}:
                </span>{" "}
                <span className="PlanDetailsPagePlanInfo__value e2e-plan-details-plan-type">
                  {t(planTypeKey)}
                </span>
              </li>
              {snpTypeKey && (
                <li>
                  <span className="PlanDetailsPagePlanInfo__label">
                    {t("plan_details.snp_type")}:
                  </span>{" "}
                  <span className="PlanDetailsPagePlanInfo__value">
                    {t(snpTypeKey)}
                  </span>
                </li>
              )}
            </>
          )}
          <li>
            <span className="PlanDetailsPagePlanInfo__label">
              {t("plan_terms.plan_id")}
            </span>{" "}
            <span className="PlanDetailsPagePlanInfo__value">
              {makePlanDisplayId(plan)}
            </span>
          </li>
        </ul>
      </div>
      {!previewMode && (
        <div className="PlanDetailsPagePlanInfo__rightCol">
          <PrintButton
            tealiumProps={{
              event_action: "Plan Details",
              event_label: "Print",
            }}
            className="PlanDetailsPagePlanInfo__printButton"
            data-testid={planDetailsPrintButtonTestId}
          />
        </div>
      )}
    </div>
  );
};
